import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BiMap, BiNote, BiDownload, BiCheckCircle, BiHourglass, BiArrowBack, BiMinusCircle, BiRefresh, BiXCircle, BiEdit } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls";
import MarkServedForm from "./ServeeAction/MarkServedForm";
import AddAttemptForm from "./ServeeAction/AddAttemptForm";
import MarkUnsuccessfulForm from "./ServeeAction/MarkUnsuccessfulForm";
import { useOnboarding } from '../Onboarding/OnboardingContext';

import apiClient from '../utils/apiClient.js';

// --- Helper for Affidavit URL ---
const getAffidavitUrl = (fileName) => {
    return `${API_BASE_URL}/uploads/affidavits/${fileName}`;
};


// --- 2. Main Component (WITH FIXES) ---

const ServeeActionComponent = ({ caseId, onAffidavitCreated }) => {
    const [serveeList, setServeeList] = useState([]);
    const [updatesHistory, setUpdatesHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [isClient, setIsClient] = useState(false);

    // GPS State (INITIALIZED TO PERSIST ACROSS RENDERS)
    const [location, setLocation] = useState({ lat: null, lng: null });
    const [locationAddress, setLocationAddress] = useState('Location not captured.');
    const [isLocationFetching, setIsLocationFetching] = useState(false); // NEW: State to manage fetching status

    const [activeServeeId, setActiveServeeId] = useState(null);
    const [activeActionType, setActiveActionType] = useState(null);

    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));

    const userId = user?.userId || user?.id;
    const currentServerName = `${user?.first_name || ''} ${user?.last_name || 'Server'}`.trim();

    const { runTour, stepIndex, setStepIndex } = useOnboarding();

    // --- NEW: Edit Location States ---
    const [isEditingLocation, setIsEditingLocation] = useState(false);
    const [editForm, setEditForm] = useState({ lat: '', lng: '', address: '' });
    const [isGeoLoading, setIsGeoLoading] = useState(false);

   // --- TOUR FIX: AUTO-SKIP GPS IF ALREADY CAPTURED ---
    // useEffect(() => {
    //     // If the tour is on Step 6 (GPS), but the location is already saved (the red button is showing),
    //     // automatically jump to Step 7 (Start Service) so the tour doesn't get stuck!
    //     if (runTour && stepIndex === 6 && location.lat) {
    //         const timer = setTimeout(() => {
    //             setStepIndex(7);
    //         }, 400);
    //         return () => clearTimeout(timer);
    //     }
    // }, [runTour, stepIndex, location.lat, setStepIndex]);

    // --- RESTORED & FIXED: Reverse Geocoding via your Backend ---
    useEffect(() => {
        // Prevent calling the API if we are using the fake Onboarding Tour data
        // We check the specific demo latitude instead of the address string to avoid loops
        if (runTour && location.lat === 25.24890) {
            return; 
        }

        if (location.lat && location.lng) {
            setLocationAddress('Reverse geocoding coordinates...');
            setIsLocationFetching(true); 
            
            axios.get(`${API_BASE_URL}/location?lat=${location.lat}&lon=${location.lng}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                setLocationAddress(response.data?.location || 'Could not find address for these coordinates.');
            })
            .catch(error => {
                console.error("API Call Error:", error);
                
                // If your token expired or the server rejected the spam, let the user know
                if (error.response?.status === 401) {
                    setLocationAddress('Session expired. Please log in again.');
                } else {
                    setLocationAddress('Error fetching address from server.');
                }
            })
            .finally(() => {
                setIsLocationFetching(false); 
            });
        }
        // CRITICAL FIX: locationAddress is REMOVED from this array to stop the infinite loop!
    }, [location.lat, location.lng, token, runTour]);


    // --- Data Fetching (Unchanged) ---

    const fetchServeeStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/servers/servees/${caseId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setServeeList(res.data.servees);
        } catch (err) {
            console.error("Error loading servee status:", err);
        }
    }, [caseId, token]);

    const fetchUpdatesHistory = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/servers/case/${caseId}/updates`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUpdatesHistory(res.data);
        } catch (err) {
            console.error("Error loading case updates:", err);
        }
    }, [caseId, token]);

    useEffect(() => {
        const role = user?.roleName;
        const isProcessServer = role === 'servers' || role === 'admin' || role === 'case_manager';
        setIsClient(role === 'client');

        if (isProcessServer || role === 'client') {
            setLoading(true);
            Promise.all([fetchServeeStatus(), fetchUpdatesHistory()]).finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, [caseId, fetchServeeStatus, fetchUpdatesHistory, user?.roleName]);

    const getLocation = () => {
        // TOUR FIX: Safely capture location and move to Step 7
        if (runTour && stepIndex === 6) {
            setLocation({ lat: 25.24890, lng: 86.97620 });
            setLocationAddress('Onboarding Demo: 6XXG+FFC, Goshalla, Bhagalpur, Bihar');
            setTimeout(() => setStepIndex(7), 500); 
            return;
        }

        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser.");
            return;
        }

        setIsLocationFetching(true);
        setLocationAddress('Attempting to get coordinates...');

        const geoOptions = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        };

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                // Notice we do NOT set isLocationFetching to false here.
                // The useEffect below will trigger, fetch the address, and then stop the loader.
                setLocation({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude
                });
            },
            (error) => {
                setIsLocationFetching(false); 
                let errorMessage = "Unable to access location.";
                if (error.code === 3) errorMessage = "GPS Timeout. Try again in an open area.";
                if (error.code === 1) errorMessage = "Please enable location permissions.";

                alert(errorMessage);
                setLocationAddress('Failed to capture coordinates.');
            },
            geoOptions
        );
    };

    const clearLocation = () => {
        setLocation({ lat: null, lng: null });
        setLocationAddress('Location not captured.');
        setIsLocationFetching(false);

        if (runTour && stepIndex === 7) {
            setStepIndex(6);
        }
    };



    // --- Core Action Logic and State Updates (FIXED) ---

    // Helper to check if a servee is globally served/completed (Unchanged)
    const isServeeFinalized = useCallback((serveeId) => {
        // Find the latest status globally from the history
        const latestUpdate = updatesHistory
            .filter(u => u.servee_id === serveeId)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

        return latestUpdate && (latestUpdate.status === 'served' || latestUpdate.status === 'completed');
    }, [updatesHistory]);

    // --- NEW: Handle Manual Location Update ---
    const handleManualLocationUpdate = async (mode) => {
        setIsGeoLoading(true);
        try {
            if (mode === 'fromCoords' && editForm.lat && editForm.lng) {
                // Fetch Address using Lat/Lng
                const res = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${editForm.lat}&lon=${editForm.lng}`);
                const fetchedAddress = res.data.display_name || 'Address not found for these coordinates';
                
                setLocation({ lat: parseFloat(editForm.lat), lng: parseFloat(editForm.lng) });
                setLocationAddress(fetchedAddress);
                setIsEditingLocation(false);

            } else if (mode === 'fromAddress' && editForm.address) {
                // Fetch Lat/Lng using Address
                const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(editForm.address)}`);
                if (res.data && res.data.length > 0) {
                    const { lat, lon, display_name } = res.data[0];
                    setLocation({ lat: parseFloat(lat), lng: parseFloat(lon) });
                    setLocationAddress(display_name);
                    setIsEditingLocation(false);
                } else {
                    alert("Could not find coordinates for this address. Try being more specific.");
                }
            } else {
                alert("Please fill in the required fields.");
            }
        } catch (error) {
            console.error("Geocoding error:", error);
            alert("Failed to fetch location data. Please try again.");
        } finally {
            setIsGeoLoading(false);
        }
    };

    // The function is fine, the error was in how it was called (missing 'await')
    const handleActionCleanup = async (message, temporaryAffidavitUpdate = null) => {
        alert(message);
        setActiveServeeId(null);
        setActiveActionType(null);
        setActionLoading(false);

        if (temporaryAffidavitUpdate) {
            const { serveeId, fileName, status } = temporaryAffidavitUpdate;

            // OPTIONAL: Immediately update the list locally for a fast UI response, 
            setServeeList(prevList => prevList.map(s => {
                if (s.id === serveeId) {
                    return {
                        ...s,
                        server_status: status,
                        latest_affidavit_file_name: fileName
                    };
                }
                return s;
            }));
        }

        // CRITICAL FIX: Use Promise.all to ensure both fetches complete before exiting the async function.
        await Promise.all([fetchUpdatesHistory(), fetchServeeStatus()]);
    }

    // Helper to prepare form data (Unchanged)
    const prepareFormData = (payload, proofFile, signatureFile = null) => {
        const formData = new FormData();
        Object.keys(payload).forEach(key => {
            if (payload[key] !== null && payload[key] !== undefined) {
                formData.append(key, String(payload[key]));
            }
        });
        formData.append("case_id", caseId);

        // Main proof file is appended as 'file'
        if (proofFile) formData.append("file", proofFile);

        // NEW: Signature file is appended as 'signature_file'
        if (signatureFile) formData.append("signature_file", signatureFile);

        return formData;
    };

    // ACTION HANDLER FIX: Added 'await' to handleActionCleanup
    const handleStartService = async (payload, file = null) => {
        if (!location.lat || !location.lng) return alert("Please capture your current location.");
        setActionLoading(true);

        const servicePayload = {
            ...payload,
            latitude: location.lat,
            longitude: location.lng
        };

        try {
            const formData = prepareFormData(servicePayload, file);
            const endpoint = `/case/${caseId}/start-service`;

            await axios.post(`${API_BASE_URL}/servers${endpoint}`, formData, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            });
            // FIX: Ensure cleanup completes before returning
            await handleActionCleanup('Service started successfully.');
            
            // ADVANCE TOUR TO STEP 8 (Mark Served) AFTER DATA REFRESH
            if (runTour && stepIndex === 7) setStepIndex(8);
            
        } catch (err) {
            console.error('start service error:', err);
            alert(`Failed to start service: ${err.response?.data?.message || err.message}`);
            setActionLoading(false);
        }
    };

    // ACTION HANDLER FIX: Added 'await' to handleActionCleanup and fixed payload merge
    const handleAddAttempt = async (payload, file = null) => {
        if (!location.lat || !location.lng) return alert("Please capture your current location.");
        setActionLoading(true);

        const endpoint = payload.current_status === 'not_started'
            ? `/case/${caseId}/start-service`
            : `/case/${caseId}/add-attempt`;

        // CRITICAL FIX: Ensure location data is merged back into the payload 
        // before preparing form data for the API call.
        const attemptPayload = {
            ...payload,
            latitude: location.lat,
            longitude: location.lng
        };

        try {
            const formData = prepareFormData(attemptPayload, file);
            await axios.post(`${API_BASE_URL}/servers${endpoint}`, formData, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            });
            // FIX: Ensure cleanup completes before returning
            await handleActionCleanup('Attempt recorded successfully.');
        } catch (err) {
            console.error('add attempt error:', err);
            alert(`Failed to record attempt: ${err.response?.data?.message || err.message}`);
            setActionLoading(false);
        }
    };

    // ACTION HANDLER FIX: Added 'await' to handleActionCleanup
    const handleMarkServed = async (payload, proofFile, signatureFile) => { // ADDED signatureFile
        // AI: TOUR INTERCEPTION - Advance to Step 10 (View Affidavit) without API call
        if (runTour && stepIndex === 9) {
            setActiveServeeId(null);
            setActiveActionType(null);
            if (onAffidavitCreated) { onAffidavitCreated(); }
            setTimeout(() => setStepIndex(10), 500);
            return;
        }

        if (!location.lat || !location.lng) return alert("Please capture your current location.");
        setActionLoading(true);

        try {
            // Now passing both files to prepareFormData
            const formData = prepareFormData(payload, proofFile, signatureFile);
            const endpoint = `/case/${caseId}/mark-served`;

            const res = await axios.post(`${API_BASE_URL}/servers${endpoint}`, formData, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            });

            // ... (Cleanup logic remains the same) ...
            const { fileName } = res.data.affidavit;
            const serveeId = payload.servee_id;
            const temporaryAffidavitUpdate = { serveeId: serveeId, fileName: fileName, status: 'served' };
            if (onAffidavitCreated) { onAffidavitCreated(); }

            // Trigger Checklist Event
            apiClient.post('/onboarding/events', { event_name: 'first_report_generated' }).catch(() => {});

            await handleActionCleanup(res.data.message || 'Servee marked served and affidavit generated.', temporaryAffidavitUpdate);
            
            // FIX: Wait 500ms for the Affidavit list to update before targeting the next button
            if (runTour && stepIndex === 9) {
                setTimeout(() => setStepIndex(10), 500); 
            }
        } catch (err) {
            console.error('mark served error:', err);
            alert(`Failed to mark served: ${err.response?.data?.message || err.message}`);
            setActionLoading(false);
        }
    };


    // ACTION HANDLER: Mark Unsuccessful (FIXED: Removed redundant prompt and ensured payload variables are correct)
    const handleMarkUnsuccessful = async (payload, proofFile, signatureFile) => {
        // AI: TOUR INTERCEPTION
        if (runTour && stepIndex === 9) {
            setActiveServeeId(null);
            setActiveActionType(null);
            if (onAffidavitCreated) { onAffidavitCreated(); }
            setTimeout(() => setStepIndex(10), 500);
            return;
        }

        // Note: 'payload' now contains servee_id and notes directly from the MarkUnsuccessfulForm
        if (!location.lat || !location.lng) return alert("Please capture your current location.");

        // CRITICAL: Since the backend requires the signature to pass validation, 
        // we must rely on the form (MarkUnsuccessfulForm) to enforce its submission 
        // and pass the signatureFile to this handler. If signatureFile is missing here, 
        // the backend will fail the request, which is the desired behavior.
        //if (!signatureFile) return alert("MANDATORY: Signature file is missing."); // Client-side check redundancy

        setActionLoading(true);

        try {
            // Construct the final payload data, merging location into the received payload
            const finalPayload = {
                ...payload, // Contains servee_id and notes
                latitude: location.lat,
                longitude: location.lng
            };

            // Prepare form data with payload, proof file (mapped to 'file'), and signature file
            const formData = prepareFormData(finalPayload, proofFile, signatureFile);
            const endpoint = `/case/${caseId}/mark-unsuccessful`;

            const res = await axios.post(`${API_BASE_URL}/servers${endpoint}`, formData, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            });

            const { fileName } = res.data.affidavit;
            const serveeId = payload.servee_id;

            const temporaryAffidavitUpdate = {
                serveeId: serveeId,
                fileName: fileName,
                status: 'completed'
            };

            if (onAffidavitCreated) {
                onAffidavitCreated();
            }

            // Trigger Checklist Event
            apiClient.post('/onboarding/events', { event_name: 'first_report_generated' }).catch(() => {});

            // Ensure cleanup completes after returning
            await handleActionCleanup(res.data.message || 'Servee marked unsuccessful and non-service affidavit generated.', temporaryAffidavitUpdate);
        } catch (err) {
            console.error('mark unsuccessful error:', err);
            alert(`Failed to mark unsuccessful: ${err.response?.data?.message || err.message}`);
            setActionLoading(false);
        }
    };

    // --- Button Callbacks for Render (Unchanged) ---

    const startServiceCallback = (serveeId) => {
        // AI: TOUR INTERCEPTION - Wait 500ms for React to swap the buttons!
       if (runTour && stepIndex === 7) {
            setServeeList(prev => prev.map(s => s.id === serveeId ? { ...s, server_status: 'in_progress' } : s));
            
            // ✅ Trigger Checklist Event
            apiClient.post('/onboarding/events', { event_name: 'first_activity_logged' }).catch(() => {});

            // Wait 500ms for React to show the Mark Served button, then move to Step 8
            setTimeout(() => setStepIndex(8), 500); 
            return;
        }

        const payload = { servee_id: serveeId, notes: `${currentServerName} starting service check-in.` };
        handleStartService(payload);
    };

    const attemptAgainCallback = (servee) => {
        const payload = { servee_id: servee.id, attempt_note: `Reopening service attempt.`, current_status: servee.server_status };
        // NOTE: The location will be correctly merged in handleAddAttempt now.
        handleAddAttempt(payload);
    };

    // --- Render Logic (FIXED FINALIZED DISPLAY) ---

    const renderActionButtons = (servee) => {
        const myStatus = servee.server_status;

        const isFinalized = isServeeFinalized(servee.id);

        const disabled = actionLoading || !location.lat;

        // 1. If a form is currently active, render the form (UPDATED UNSUCCESSFUL FORM)
        if (activeServeeId === servee.id) {
            if (activeActionType === 'served') {
                return (
                    <MarkServedForm
                        servee={servee}
                        location={location}
                        // Updated onSubmit to pass 3 arguments
                        onSubmit={(payload, proofFile, signatureFile) => handleMarkServed(payload, proofFile, signatureFile)}
                        onCancel={() => setActiveServeeId(null)}
                        isLoading={actionLoading}
                        currentServerName={currentServerName}
                    />
                );
            }
            if (activeActionType === 'unsuccessful') { // NEW: Handle the unsuccessful form state
                return (
                    <MarkUnsuccessfulForm
                        servee={servee}
                        location={location}
                        // Updated onSubmit to pass 3 arguments
                        onSubmit={(payload, proofFile, signatureFile) => handleMarkUnsuccessful(payload, proofFile, signatureFile)}
                        onCancel={() => setActiveServeeId(null)}
                        isLoading={actionLoading}
                    />
                );
            }
            if (activeActionType === 'attempt') {
                return (
                    <AddAttemptForm
                        servee={servee}
                        location={location}
                        onSubmit={handleAddAttempt}
                        onCancel={() => setActiveServeeId(null)}
                        isLoading={actionLoading}
                    />
                );
            }
            return null;
        }

        // 2. Check global finalization first
        if (isFinalized) {
            const finalUpdate = updatesHistory.filter(u => u.servee_id === servee.id).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            const finalStatus = finalUpdate?.status;
            // const affidavitFileName = finalUpdate?.file_name || servee.latest_affidavit_file_name;
            // const affidavitDownloadLink = affidavitFileName ? getAffidavitUrl(affidavitFileName) : null;

            return (
                <div className="flex space-x-2 items-center">
                    <span className={`text-sm font-semibold ${finalStatus === 'served' ? 'text-green-600' : 'text-red-600'}`}>
                        {finalStatus === 'served' ? '✅ Service Finalized' : '❌ Unsuccessful Finalized'}
                    </span>
                    <button onClick={() => attemptAgainCallback(servee)} className="btn-action bg-purple-600 text-white" disabled={disabled}>
                        <BiArrowBack /> Attempt Again
                    </button>
                </div>
            );
        }

        // 3. If not finalized, proceed with the current server's workflow
        switch (myStatus) {
            case 'not_started':
                return (
                    <div className="flex flex-wrap space-x-2">
                        {/* MAKE SURE .tour-start-service IS ONLY ON THIS BUTTON */}
                        <button 
                            onClick={() => startServiceCallback(servee.id)} 
                            className="btn-action bg-blue-600 text-white tour-start-service" 
                            disabled={disabled}
                        >
                            <BiCheckCircle /> Start Service (Check-in)
                        </button>
                    </div>
                );
            case 'in_progress':
            case 'attempted':
                return (
                    <div className="flex flex-wrap space-x-2">
                        <button onClick={() => { setActiveServeeId(servee.id); setActiveActionType('attempt'); }} className="btn-action bg-yellow-600 text-white" disabled={disabled}>
                            <BiHourglass /> Add Attempt
                        </button>
                        <div className="tour-mark-served">
                            <button onClick={() => { 
                                setActiveServeeId(servee.id); 
                                setActiveActionType('served'); 
                                // TOUR FLOW 3: User clicks Mark Served. Modal opens. Move to Step 9.
                                if (runTour && stepIndex === 8) {
                                    setTimeout(() => setStepIndex(9), 400); 
                                }
                            }} className="btn-action bg-green-600 text-white" disabled={disabled}>
                                <BiCheckCircle /> Mark Served
                            </button>
                        </div>
                        <button onClick={() => { setActiveServeeId(servee.id); setActiveActionType('unsuccessful'); }} className="btn-action bg-red-600 text-white" disabled={disabled}>
                            <BiMinusCircle /> Mark Unsuccessful (Finalize)
                        </button>
                    </div>
                );
            default:
                return null;
        }
    };

    // Helper to enrich history timeline with names (Unchanged)
    const getNames = (update) => {
        const servee = serveeList.find(s => s.id === update.servee_id);
        const serveeName = servee ? servee.full_name : 'Case-level Update';

        const serverFullName = update.server_first_name && update.server_last_name
            ? `${update.server_first_name} ${update.server_last_name}`.trim()
            : `Server ${update.server_id}`;

        const serverName = (update.server_id === userId) ? currentServerName : serverFullName;

        return { serveeName, serverName };
    }

    if (loading) return <div className="text-center p-4">Loading process serve data...</div>;
    if (!userId) return <div className="text-center p-4 text-red-600">Authentication failed. Please log in.</div>;


    return (
        <div className="p-6 bg-white shadow-lg rounded-xl space-y-8">
            <style jsx="true">{`
                 .btn-action {
                     display: flex;
                     align-items: center;
                     padding: 8px 12px;
                     border-radius: 6px;
                     font-size: 0.875rem;
                     font-weight: 600;
                     transition: background-color 0.15s;
                     margin-top: 8px;
                 }
                 .btn-action:hover:not(:disabled) {
                     filter: brightness(1.1);
                 }
                 .btn-action:disabled {
                     opacity: 0.5;
                     cursor: not-allowed;
                 }
                 .btn-action svg {
                     margin-right: 4px;
                 }
            `}</style>

            <h2 className="text-2xl font-bold text-gray-800 flex items-center"><BiNote className="mr-2" />
                {isClient ? 'Process Service Attempts' : 'Process Service Management'}</h2>

            {!isClient && (

                <>
                    <hr />
                    {/* --- Location Capture Section (UPDATED WITH EDIT) --- */}
                    <div className="tour-gps-checkin" style={{ width: '100%' }}>
                        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="text-lg font-semibold text-blue-800 flex items-center"><BiMap className="mr-2" /> Current GPS Check-in</h3>
                                
                                {/* Edit Toggle Button */}
                                <button 
                                    onClick={() => {
                                        setEditForm({ lat: location.lat || '', lng: location.lng || '', address: locationAddress !== 'Location not captured.' ? locationAddress : '' });
                                        setIsEditingLocation(!isEditingLocation);
                                    }} 
                                    className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-semibold"
                                >
                                    <BiEdit className="mr-1" /> {isEditingLocation ? 'Cancel Edit' : 'Edit Location'}
                                </button>
                            </div>

                            {/* --- EDIT FORM (Mobile Responsive) --- */}
                            {isEditingLocation ? (
                                <div className="space-y-4 bg-white p-4 rounded-md border border-blue-100 shadow-sm mt-3">
                                    <p className="text-sm text-gray-600 font-medium">Update via Coordinates <span className="text-blue-500">OR</span> Update via Address</p>
                                    
                                    <div className="flex flex-col md:flex-row gap-4">
                                        {/* Coord Inputs */}
                                        <div className="flex-1 space-y-2">
                                            <input type="number" placeholder="Latitude" value={editForm.lat} onChange={(e) => setEditForm({...editForm, lat: e.target.value})} className="w-full p-2 border rounded text-sm" />
                                            <input type="number" placeholder="Longitude" value={editForm.lng} onChange={(e) => setEditForm({...editForm, lng: e.target.value})} className="w-full p-2 border rounded text-sm" />
                                            <button 
                                                onClick={() => handleManualLocationUpdate('fromCoords')} 
                                                disabled={isGeoLoading}
                                                className="w-full bg-indigo-600 text-white py-1.5 rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                {isGeoLoading ? 'Searching...' : 'Find Address from Coords'}
                                            </button>
                                        </div>

                                        <div className="hidden md:block w-px bg-gray-200"></div>

                                        {/* Address Input */}
                                        <div className="flex-1 space-y-2">
                                            <textarea 
                                                placeholder="Enter full address..." 
                                                value={editForm.address} 
                                                onChange={(e) => setEditForm({...editForm, address: e.target.value})} 
                                                className="w-full p-2 border rounded text-sm h-[88px] resize-none"
                                            />
                                            <button 
                                                onClick={() => handleManualLocationUpdate('fromAddress')} 
                                                disabled={isGeoLoading}
                                                className="w-full bg-teal-600 text-white py-1.5 rounded text-sm hover:bg-teal-700 disabled:opacity-50"
                                            >
                                                {isGeoLoading ? 'Searching...' : 'Find Coords from Address'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* --- DEFAULT VIEW --- */
                                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                                    {!location.lat ? (
                                        <button
                                            onClick={getLocation}
                                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center w-full sm:w-auto justify-center"
                                            disabled={actionLoading || isLocationFetching}
                                        >
                                            {isLocationFetching ? <><BiRefresh className="mr-2 animate-spin" /> Capturing...</> : 'Capture Live Location'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={clearLocation}
                                            className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center w-full sm:w-auto justify-center"
                                            disabled={actionLoading}
                                        >
                                            <BiXCircle className="mr-2" /> Clear Location
                                        </button>
                                    )}

                                    <div className="text-sm w-full">
                                        <p className="font-medium">Coordinates: {location.lat ? `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}` : 'Not captured'}</p>
                                        <p className="text-gray-600">Address: <span className="font-semibold text-blue-700">{locationAddress}</span></p>
                                    </div>
                                </div>
                            )}
                            
                            {!location.lat && !isEditingLocation && <p className="text-sm text-red-500 mt-2">❗ Location must be captured or entered manually before any action.</p>}
                        </div>
                    </div>

                    {/* --- Servee Status and Actions (Unchanged) --- */}
                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-800">Servees and Actions</h3>
                        {serveeList.length > 0 ? serveeList.map((servee) => (
                            <div key={servee.id} className="p-4 border border-gray-200 rounded-lg shadow-sm">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="font-bold text-lg">{servee.full_name || `Servee ID: ${servee.id}`}</h4>

                                    {/* Display Status: Use the global check for FINALIZED */}
                                    {isServeeFinalized(servee.id) ? (
                                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${servee.server_status === 'served' || servee.server_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {'FINALIZED'}
                                        </span>
                                    ) : servee.server_status !== 'not_started' && (
                                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${servee.server_status === 'served' ? 'bg-green-100 text-green-700' : servee.server_status === 'completed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                            {servee.server_status.toUpperCase().replace('_', ' ')}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 mb-3">Latest Action Address: {locationAddress || 'N/A'}</p>
                                <div className="tour-action-container mt-3">
                                    {renderActionButtons(servee)}
                                </div>
                            </div>
                        )) : <p className="text-gray-500">No servees found for this case.</p>}
                    </div>
                    <hr />
                </>

            )}

            {/* --- Updates History Timeline (Unchanged) --- */}
            <div className="mt-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Activity Timeline</h3>
                {updatesHistory.length > 0 ? (
                    <ul className="space-y-3">
                        {updatesHistory.map((u) => {
                            const { serveeName } = getNames(u);

                            const isAffidavitFile = (u.status === 'served' || u.status === 'completed') && u.file_name && u.file_path && u.file_path.includes('affidavits');

                            const historyFileDownloadLink = isAffidavitFile
                                ? getAffidavitUrl(u.file_name)
                                : u.file_path ? `${API_BASE_URL}/files/download?path=${u.file_path}` : null;

                            return (
                                <li key={u.id} className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                    <p className="text-sm text-gray-800 font-medium">
                                        <span className="font-bold text-indigo-700 mr-2">[{u.status ? u.status.toUpperCase() : 'CHECK-IN'}]</span>
                                        {`Activity ${u.servee_id ? `for ${serveeName}` : '(Case-level)'}: `}
                                        <span className="font-normal">{u.notes}</span>
                                    </p>

                                    {u.file_name && historyFileDownloadLink && (
                                        <a
                                            href={historyFileDownloadLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:text-blue-900 flex items-center mt-1 space-x-1"
                                        >
                                            <BiDownload className="w-3 h-3" />
                                            <span>Download {isAffidavitFile ? `Affidavit: ${u.file_name}` : u.file_name}</span>
                                        </a>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                        {u.address ? `📍Location: ${u.address}` : 'Location unavailable'}
                                        {" • "}
                                        {new Date(u.created_at).toLocaleString()}
                                    </p>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="text-sm text-gray-500">No activity recorded yet.</p>
                )}
            </div>
        </div>
    );
};

export default ServeeActionComponent;
